import { useState } from 'react';
import { Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { exportPosReport, PosReportFileType } from '~/api/review';

const parseErrorPayload = async (data: unknown): Promise<Record<string, string> | null> => {
  if (!data) return null;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      return JSON.parse(text);
    } catch {
      return null;
    }
  }
  if (typeof data === 'object') return data as Record<string, string>;
  return null;
};

const getErrorMessage = (
  payload: Record<string, string> | null,
  isArabic: boolean,
  fallback: string,
) => {
  if (!payload) return fallback;
  return (
    (isArabic ? payload.arDescription : undefined) ??
    payload.description ??
    payload.message ??
    fallback
  );
};

const extractFilename = (disposition: string, fallback: string) => {
  const match = disposition.match(/filename\*?=(?:UTF-8'')?\"?([^\";\n]+)\"?/i);
  return match?.[1] ?? fallback;
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

/**
 * Shared hook for downloading POS reports (bank / registration).
 * Used by both the desktop AgentFilters and the mobile AgentFiltersButton drawer.
 */
const useDownloadPosReport = () => {
  const { t, i18n } = useTranslation('agent');
  const [downloadingReport, setDownloadingReport] = useState<PosReportFileType | null>(null);

  const handleDownloadPosReport = async (fileType: PosReportFileType) => {
    setDownloadingReport(fileType);
    try {
      const response = await exportPosReport(fileType);
      const disposition = (response.headers['content-disposition'] as string) ?? '';
      const defaultName =
        fileType === 'registration' ? 'TMS-Registration-Report.xlsx' : 'Bank-Report.xlsx';
      const filename = extractFilename(disposition, defaultName);
      downloadBlob(response.data, filename);
    } catch (error: any) {
      console.error('Error downloading report:', error);
      const isArabic = i18n.language?.slice(0, 2) === 'ar';
      const fallback = t('An error occurred while downloading the report. Please try again.');
      const payload = await parseErrorPayload(error?.response?.data);
      const apiMessage = getErrorMessage(payload, isArabic, error?.message || fallback);
      Modal.error({ title: t('Download Failed'), content: apiMessage });
    } finally {
      setDownloadingReport(null);
    }
  };

  return { downloadingReport, handleDownloadPosReport };
};

export default useDownloadPosReport;
